/**
 * DiagramRenderer
 * Renders Mermaid diagrams with zoom, pan, and responsive behavior
 */

import { useEffect, useRef, useState, useCallback } from "react";
import mermaid from "mermaid";
import { Button } from "@learn-me/ui";
import {
  ZoomIn,
  ZoomOut,
  Maximize2,
  RotateCcw,
  Code,
  AlertCircle,
  Loader2,
} from "lucide-react";

// Initialize mermaid with default config
mermaid.initialize({
  startOnLoad: false,
  theme: "default",
  securityLevel: "loose",
  fontFamily: "inherit",
});

interface DiagramRendererProps {
  code: string;
  title?: string;
  className?: string;
  showControls?: boolean;
  showCode?: boolean;
}

export function DiagramRenderer({
  code,
  title,
  className = "",
  showControls = true,
  showCode = false,
}: DiagramRendererProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [svg, setSvg] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [zoom, setZoom] = useState(1);
  const [showCodeView, setShowCodeView] = useState(showCode);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Generate unique ID for the diagram
  const diagramId = useRef(`mermaid-${Math.random().toString(36).slice(2, 9)}`);

  // Render the diagram
  const renderDiagram = useCallback(async () => {
    if (!code.trim()) {
      setError("No hay código de diagrama");
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Validate syntax first
      const isValid = await mermaid.parse(code);
      if (!isValid) {
        throw new Error("Sintaxis de diagrama inválida");
      }

      // Render the diagram
      const { svg: renderedSvg } = await mermaid.render(
        diagramId.current,
        code,
      );
      setSvg(renderedSvg);
    } catch (err) {
      console.error("Error rendering Mermaid diagram:", err);
      setError(
        err instanceof Error ? err.message : "Error al renderizar el diagrama",
      );
    } finally {
      setIsLoading(false);
    }
  }, [code]);

  useEffect(() => {
    renderDiagram();
  }, [renderDiagram]);

  // Zoom controls
  const handleZoomIn = () => setZoom((z) => Math.min(z + 0.25, 3));
  const handleZoomOut = () => setZoom((z) => Math.max(z - 0.25, 0.5));
  const handleResetZoom = () => setZoom(1);

  // Fullscreen toggle
  const handleFullscreen = () => {
    if (!containerRef.current) return;

    if (!isFullscreen) {
      if (containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  // Loading state
  if (isLoading) {
    return (
      <div
        className={`flex items-center justify-center p-8 bg-muted/30 rounded-lg ${className}`}
      >
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-muted-foreground">Cargando diagrama...</span>
      </div>
    );
  }

  // Error state with code fallback
  if (error) {
    return (
      <div className={`rounded-lg border border-red-200 ${className}`}>
        <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 text-sm rounded-t-lg">
          <AlertCircle className="h-4 w-4" />
          <span>Error al renderizar el diagrama</span>
        </div>
        <div className="p-4">
          <p className="text-sm text-red-600 mb-3">{error}</p>
          <details className="text-sm">
            <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
              Ver código del diagrama
            </summary>
            <pre className="mt-2 p-3 bg-muted rounded text-xs overflow-x-auto">
              {code}
            </pre>
          </details>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`relative rounded-lg border bg-white ${className} ${
        isFullscreen ? "fixed inset-0 z-50 m-0 rounded-none" : ""
      }`}
    >
      {/* Header with title and controls */}
      {(title || showControls) && (
        <div className="flex items-center justify-between p-2 border-b bg-muted/30">
          {title && (
            <span className="text-sm font-medium text-muted-foreground">
              {title}
            </span>
          )}
          {showControls && (
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleZoomOut}
                className="h-7 w-7 p-0"
                title="Alejar"
              >
                <ZoomOut className="h-4 w-4" />
              </Button>
              <span className="text-xs text-muted-foreground min-w-[3rem] text-center">
                {Math.round(zoom * 100)}%
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleZoomIn}
                className="h-7 w-7 p-0"
                title="Acercar"
              >
                <ZoomIn className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleResetZoom}
                className="h-7 w-7 p-0"
                title="Restablecer zoom"
              >
                <RotateCcw className="h-4 w-4" />
              </Button>
              <div className="w-px h-4 bg-border mx-1" />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowCodeView(!showCodeView)}
                className={`h-7 w-7 p-0 ${showCodeView ? "bg-muted" : ""}`}
                title="Ver código"
              >
                <Code className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleFullscreen}
                className="h-7 w-7 p-0"
                title="Pantalla completa"
              >
                <Maximize2 className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Diagram content */}
      <div
        className={`overflow-auto ${isFullscreen ? "h-[calc(100vh-40px)]" : "min-h-[350px] max-h-[600px]"}`}
      >
        {showCodeView ? (
          <pre className="p-4 text-sm overflow-x-auto bg-muted/20">
            <code>{code}</code>
          </pre>
        ) : (
          <div
            className="p-6 flex items-center justify-center min-h-[350px]"
            style={{
              transform: `scale(${zoom})`,
              transformOrigin: "center top",
            }}
          >
            <div
              dangerouslySetInnerHTML={{ __html: svg }}
              className="mermaid-diagram [&_svg]:max-w-full"
            />
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Simple diagram display without controls (for inline use)
 */
export function DiagramInline({
  code,
  className = "",
}: {
  code: string;
  className?: string;
}) {
  return (
    <DiagramRenderer code={code} className={className} showControls={false} />
  );
}
